import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Pignan' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Pignan',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Pignan' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Pignan. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous sur les menuiseries bois du centre ancien de Pignan ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons sur les fenêtres bois et menuiseries anciennes du coeur de Pignan. Nous savons adapter les réparations aux contraintes du bâti ancien : crémones traditionnelles, vitrages simples à remplacer par du double vitrage sur mesure, dimensions hors standard.',
      },
    },
    {
      '@type': 'Question',
      name: 'Proposez-vous la motorisation des volets roulants à Pignan ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous motorisons les volets roulants manuels. C\'est une demande fréquente à Pignan, notamment dans les maisons de village où le confort de la motorisation remplace avantageusement la sangle. Devis gratuit, intervention rapide.',
      },
    },
  ],
};

export default function PignanPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Pignan | DFPRO"
        description="Fenêtre bois ancienne, baie coulissante ou volet roulant en panne à Pignan ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-pignan/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Pignan"
        subtitle="Village vigneron à l'ouest de Montpellier : nos techniciens réparent fenêtres bois, baies coulissantes et volets roulants à Pignan. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Fenêtre ancienne ou volet en panne à Pignan ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens interviennent à Pignan et dans l'ouest montpelliérain, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Pignan, village vigneron à l'ouest de Montpellier, possède un centre ancien avec de nombreuses menuiseries bois qui demandent un entretien spécifique. Nous intervenons aussi sur les baies coulissantes et volets des résidences récentes. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/pignan-baie-01.avif"
              alt="Réparation de baie vitrée coulissante à Pignan"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante et fenêtre bois : double expertise</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              À Pignan, nous passons des <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">roulettes de baie</Link> dans une résidence récente à la <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">réparation de fenêtre bois</Link> dans une maison de village. Deux mondes, une seule équipe.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>. Fenêtres : <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémone</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant : motorisation des volets manuels</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Beaucoup de maisons de village à Pignan sont encore équipées de <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants manuels</Link> à sangle. Nous proposons la <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> pour gagner en confort.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Pannes : <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">blocage</Link>, <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link>.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/pignan-volet-02.jpg"
              alt="Motorisation de volet roulant à Pignan"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/pignan-vitrage-03.avif"
              alt="Remplacement de vitrage à Pignan"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier : simple vitrage vers double vitrage</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les fenêtres anciennes de Pignan sont souvent équipées de simple vitrage. Nous posons du <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> sur mesure qui améliore considérablement l'isolation thermique.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link>, <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">urgence</Link>, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Pignan sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Pignan : centre ancien, lotissements, zones d'activités. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, Cournonterral, Murviel-lès-Montpellier. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}